import React, { useState, useMemo, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { 
  Plus, User, Loader2, Sparkles, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, Film, Box, Layers, Users,
  GripVertical, Play, Search, FolderOpen, Folder, 
  ChevronDown, Star, Globe, Lock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// ============== TABS ==============
const TABS = [
  { id: 'characters', label: 'Characters', icon: Users },
  { id: 'animations', label: 'Animations', icon: Film },
  { id: 'sets', label: 'Sets', icon: Layers },
  { id: 'items', label: 'Items', icon: Box },
];

// ============== CHARACTER ITEM ==============
function CharacterItem({ character, isSelected, onClick, onDelete, onDragStart }) {
  const isProcessing = ['converting', 'rigging', 'retexturing', 'created'].includes(character.status);
  const isReady = ['styled', 'rigged', 'model_ready'].includes(character.status);
  const hasFailed = character.status?.includes('failed');

  const getProgress = () => {
    if (character.status === 'converting') return character.progress || 10;
    if (character.status === 'rigging') return 40 + (character.rig_progress || 0) * 0.3;
    if (character.status === 'retexturing') return 70 + (character.retexture_progress || 0) * 0.3;
    if (isReady) return 100;
    return 0;
  };

  const getStatusText = () => {
    if (character.status === 'converting') return 'Converting to 3D...';
    if (character.status === 'rigging') return 'Adding skeleton...';
    if (character.status === 'retexturing') return 'Applying style...';
    if (character.status === 'styled') return 'Ready';
    if (character.status === 'rigged') return 'Ready';
    if (character.status === 'model_ready') return 'Ready (no rig)';
    if (hasFailed) return 'Failed';
    return character.status;
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDelete();
  };

  return (
    <div
      onClick={isReady ? onClick : undefined}
      draggable={isReady}
      onDragStart={(e) => {
        if (isReady && onDragStart) {
          e.dataTransfer.setData('application/json', JSON.stringify({
            type: 'character',
            character: character
          }));
          onDragStart(character);
        }
      }}
      className={`
        group relative p-2 rounded-lg cursor-pointer transition-all
        ${isSelected ? 'bg-purple-600/30 border border-purple-500' : 'bg-slate-800 hover:bg-slate-700 border border-transparent'}
        ${!isReady && !isProcessing ? 'opacity-50' : ''}
        ${isProcessing ? 'cursor-wait' : ''}
        ${isReady ? 'cursor-grab active:cursor-grabbing' : ''}
      `}
      data-testid={`character-item-${character.id}`}
    >
      <div className="flex items-start gap-2">
        {/* Thumbnail */}
        <div className="w-10 h-10 rounded bg-slate-700 overflow-hidden flex-shrink-0">
          {character.thumbnail_url || character.source_image_url ? (
            <img 
              src={character.thumbnail_url || character.source_image_url} 
              alt={character.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User className="w-5 h-5 text-slate-500" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-white truncate">{character.name}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <p className={`text-xs ${hasFailed ? 'text-red-400' : 'text-slate-400'}`}>
              {getStatusText()}
            </p>
            {isProcessing && <Loader2 className="w-3 h-3 text-purple-400 animate-spin" />}
            {isReady && <Sparkles className="w-3 h-3 text-green-400" />}
          </div>
          
          {isProcessing && (
            <div className="mt-1">
              <Progress value={getProgress()} className="h-1" />
            </div>
          )}
        </div>

        {/* Delete button */}
        {isReady && (
          <button
            onClick={handleDeleteClick}
            className="p-1.5 rounded bg-slate-600 hover:bg-red-500 text-slate-300 hover:text-white transition-colors flex-shrink-0"
            title="Delete character"
            data-testid={`delete-character-${character.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============== ANIMATION ITEM (Draggable with Add Button) ==============
function AnimationItem({ animation, onDragStart, onAddAtPlayhead, hasSelectedCharacter, onDelete }) {
  const isStock = animation.is_stock;
  const isBlankTemplate = animation.is_blank_template;
  const isBuiltIn = animation.is_builtin;
  const canDelete = !isStock && !isBuiltIn && !isBlankTemplate; // Only signature clips can be deleted
  
  const handleAddClick = (e) => {
    e.stopPropagation();
    if (onAddAtPlayhead) {
      onAddAtPlayhead(animation);
    }
  };
  
  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(animation);
    }
  };
  
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/json', JSON.stringify({
          type: 'animation',
          animation: animation
        }));
        if (onDragStart) onDragStart(animation);
      }}
      className="group p-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-transparent hover:border-purple-500/50 cursor-grab active:cursor-grabbing transition-all"
      data-testid={`animation-item-${animation.id || animation.name}`}
    >
      <div className="flex items-center gap-2">
        {/* Drag handle */}
        <GripVertical className="w-4 h-4 text-slate-500 group-hover:text-slate-300 flex-shrink-0" />
        
        {/* Icon */}
        <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
          isBlankTemplate ? 'bg-amber-600/30' : 'bg-purple-600/30'
        }`}>
          {isBlankTemplate ? (
            <Plus className="w-4 h-4 text-amber-400" />
          ) : (
            <Play className="w-4 h-4 text-purple-400" />
          )}
        </div>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-white truncate">{animation.name}</p>
          <p className="text-xs text-slate-500">
            {isBlankTemplate ? '10s blank' : animation.total_frames ? `${(animation.total_frames / (animation.fps || 24)).toFixed(1)}s` : 'Drag to timeline'}
          </p>
        </div>
        
        {/* Badge */}
        {isStock && !isBlankTemplate && (
          <span className="text-xs text-purple-400 px-1.5 py-0.5 bg-purple-500/20 rounded flex-shrink-0">
            Essential
          </span>
        )}
        
        {isBlankTemplate && (
          <span className="text-xs text-emerald-400 px-1.5 py-0.5 bg-emerald-500/20 rounded flex-shrink-0">
            Create
          </span>
        )}
        
        {/* Delete button - only for signature clips */}
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDeleteClick}
            className="h-6 w-6 p-0 text-slate-400 hover:text-red-400 hover:bg-red-500/20 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            title="Delete animation"
            data-testid={`delete-animation-${animation.id || animation.name}`}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        )}
        
        {/* Add at Playhead button */}
        <Button
          size="sm"
          variant="ghost"
          onClick={handleAddClick}
          disabled={!hasSelectedCharacter}
          className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-purple-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          title={hasSelectedCharacter ? "Add at playhead" : "Select a character first"}
          data-testid={`add-animation-${animation.id || animation.name}`}
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// ============== ANIMATION FOLDER ==============
function AnimationFolder({ 
  name, 
  icon: Icon, 
  animations, 
  isOpen, 
  onToggle, 
  onDragStart, 
  onAddAtPlayhead,
  hasSelectedCharacter,
  onDeleteAnimation
}) {
  const count = animations?.length || 0;
  
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-800 transition-colors"
        data-testid={`folder-${name.toLowerCase()}`}
      >
        {isOpen ? (
          <ChevronDown className="w-3 h-3 text-slate-400" />
        ) : (
          <ChevronRight className="w-3 h-3 text-slate-400" />
        )}
        <Icon className="w-4 h-4 text-slate-400" />
        <span className="text-xs font-medium text-slate-300 flex-1 text-left">{name}</span>
        <span className="text-xs text-slate-500">{count}</span>
      </button>
      
      {isOpen && (
        <div className="ml-5 mt-1 space-y-1">
          {animations?.length === 0 ? (
            <p className="text-xs text-slate-500 px-2 py-1 italic">No animations</p>
          ) : (
            animations?.map((anim) => (
              <AnimationItem
                key={anim.id || anim.name}
                animation={anim}
                onDragStart={onDragStart}
                onAddAtPlayhead={onAddAtPlayhead}
                hasSelectedCharacter={hasSelectedCharacter}
                onDelete={onDeleteAnimation}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============== PLACEHOLDER TAB CONTENT ==============
function PlaceholderContent({ title, description }) {
  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="text-center text-slate-500">
        <Box className="w-10 h-10 mx-auto mb-2 opacity-30" />
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs mt-1">{description}</p>
      </div>
    </div>
  );
}

// ============== MAIN ASSET LIBRARY ==============
export default function AssetLibrary({
  // Characters
  characters = [],
  selectedCharacter,
  onSelectCharacter,
  onCreateNew,
  onRefresh,
  onDeleteCharacter,
  loading,
  // Animations (legacy - kept for compatibility)
  animations = [],
  builtInAnimations = [],
  // New animation library (folder-based)
  animationLibrary = { essentials: [], signature: [], community: [] },
  animationSearchQuery = '',
  onAnimationSearch,
  onRefreshAnimations,
  refreshTrigger = 0,
  // User info for delete
  userId,
  // Add at playhead
  currentFrame = 0,
  selectedTimelineCharacter,
  onAddAnimationAtPlayhead,
  // Animation drag
  onAnimationDragStart,
  // Collapse state
  isCollapsed,
  onToggleCollapse
}) {
  const [activeTab, setActiveTab] = useState('characters');
  const [openFolders, setOpenFolders] = useState({
    essentials: true,
    signature: true,
    community: false
  });
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  
  // Delete confirmation modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [animationToDelete, setAnimationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Refresh animation library when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0 && onRefreshAnimations) {
      onRefreshAnimations();
    }
  }, [refreshTrigger, onRefreshAnimations]);
  
  // Handle delete animation click - show confirmation modal
  const handleDeleteAnimationClick = (animation) => {
    setAnimationToDelete(animation);
    setDeleteModalOpen(true);
  };
  
  // Confirm delete animation
  const handleConfirmDelete = async () => {
    if (!animationToDelete?.id) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/api/animations/${animationToDelete.id}`, {
        params: { user_id: userId }
      });
      toast.success(`"${animationToDelete.name}" deleted`);
      setDeleteModalOpen(false);
      setAnimationToDelete(null);
      // Refresh the animation library
      if (onRefreshAnimations) {
        onRefreshAnimations();
      }
    } catch (error) {
      console.error('Failed to delete animation:', error);
      // Handle error detail which can be a string or array of objects
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to delete animation';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || errorMsg;
      }
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle folder open/close
  const toggleFolder = (folderName) => {
    setOpenFolders(prev => ({
      ...prev,
      [folderName]: !prev[folderName]
    }));
  };

  // Handle search input
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setLocalSearchQuery(query);
    if (onAnimationSearch) {
      onAnimationSearch(query);
    }
  };

  // Filter animations based on search query (client-side filtering for immediate feedback)
  const filteredAnimationLibrary = useMemo(() => {
    if (!localSearchQuery.trim()) {
      return animationLibrary;
    }
    
    const query = localSearchQuery.toLowerCase();
    const filterAnimations = (animations) => 
      animations?.filter(a => 
        a.name?.toLowerCase().includes(query) ||
        a.description?.toLowerCase().includes(query)
      ) || [];
    
    return {
      essentials: filterAnimations(animationLibrary.essentials),
      signature: filterAnimations(animationLibrary.signature),
      community: filterAnimations(animationLibrary.community)
    };
  }, [animationLibrary, localSearchQuery]);

  // Check if there's a selected character on the timeline
  const hasSelectedCharacter = !!selectedTimelineCharacter;

  // Combine old animations into essentials if animationLibrary is empty
  const effectiveLibrary = useMemo(() => {
    // If we have data from the API, use it
    if (animationLibrary.essentials?.length > 0 || 
        animationLibrary.signature?.length > 0 || 
        animationLibrary.community?.length > 0) {
      return filteredAnimationLibrary;
    }
    
    // Fallback to old format - combine built-in and custom animations
    const allAnimations = [
      ...builtInAnimations.map(a => ({ ...a, is_stock: true, is_builtin: true })),
      ...animations
    ];
    
    return {
      essentials: allAnimations.filter(a => a.is_stock || a.is_builtin),
      signature: allAnimations.filter(a => !a.is_stock && !a.is_builtin),
      community: []
    };
  }, [animationLibrary, filteredAnimationLibrary, builtInAnimations, animations]);

  if (isCollapsed) {
    return (
      <div className="w-12 bg-slate-900 border-r border-slate-700 flex flex-col items-center py-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onToggleCollapse}
          className="h-8 w-8 p-0 text-slate-400 hover:text-white mb-2"
          title="Expand Library"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
        
        {/* Vertical tab icons */}
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <Button
              key={tab.id}
              size="sm"
              variant={activeTab === tab.id ? 'secondary' : 'ghost'}
              onClick={() => {
                setActiveTab(tab.id);
                onToggleCollapse();
              }}
              className="h-8 w-8 p-0 text-slate-400 hover:text-white mb-1"
              title={tab.label}
            >
              <Icon className="w-4 h-4" />
            </Button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="w-64 bg-slate-900 border-r border-slate-700 flex flex-col h-full">
      {/* Header with collapse button */}
      <div className="p-2 border-b border-slate-700 flex items-center justify-between">
        <span className="text-sm font-semibold text-white">Assets</span>
        <Button
          size="sm"
          variant="ghost"
          onClick={onToggleCollapse}
          className="h-7 w-7 p-0 text-slate-400 hover:text-white"
          title="Collapse Library"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-700">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 px-1 text-xs font-medium transition-colors flex flex-col items-center gap-1
                ${activeTab === tab.id 
                  ? 'text-purple-400 border-b-2 border-purple-400 bg-purple-500/10' 
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              data-testid={`tab-${tab.id}`}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'characters' && (
        <>
          {/* Character actions */}
          <div className="p-2 border-b border-slate-700 flex gap-1">
            <Button 
              size="sm" 
              onClick={onCreateNew}
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs"
              data-testid="create-new-character-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              New
            </Button>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={onRefresh}
              className="h-8 w-8 p-0 text-slate-400 hover:text-white"
              data-testid="refresh-characters-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {/* Character list */}
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {characters.length === 0 ? (
                <div className="text-center py-6 text-slate-500 text-xs">
                  <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No characters yet</p>
                  <p className="mt-1 opacity-70">Create your first character</p>
                </div>
              ) : (
                characters.map((char) => (
                  <CharacterItem
                    key={char.id}
                    character={char}
                    isSelected={selectedCharacter?.id === char.id}
                    onClick={() => onSelectCharacter(char)}
                    onDelete={() => onDeleteCharacter(char)}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </>
      )}

      {activeTab === 'animations' && (
        <>
          {/* Search bar */}
          <div className="p-2 border-b border-slate-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                type="text"
                placeholder="Search animations..."
                value={localSearchQuery}
                onChange={handleSearchChange}
                className="h-8 pl-7 text-xs bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="animation-search-input"
              />
            </div>
          </div>
          
          {/* Refresh button + info */}
          <div className="px-2 py-1.5 border-b border-slate-700 flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {hasSelectedCharacter 
                ? `Adding to: ${selectedTimelineCharacter?.name}` 
                : 'Select a character on timeline'}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onRefreshAnimations && onRefreshAnimations()}
              className="h-6 w-6 p-0 text-slate-400 hover:text-white"
              title="Refresh animations"
              data-testid="refresh-animations-btn"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>

          {/* Animation folders */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              {/* Essentials folder - Stock animations */}
              <AnimationFolder
                name="Essentials"
                icon={Star}
                animations={effectiveLibrary.essentials}
                isOpen={openFolders.essentials}
                onToggle={() => toggleFolder('essentials')}
                onDragStart={onAnimationDragStart}
                onAddAtPlayhead={onAddAnimationAtPlayhead}
                hasSelectedCharacter={hasSelectedCharacter}
              />
              
              {/* Signature folder - User's own animations */}
              <AnimationFolder
                name="Signature"
                icon={Lock}
                animations={effectiveLibrary.signature}
                isOpen={openFolders.signature}
                onToggle={() => toggleFolder('signature')}
                onDragStart={onAnimationDragStart}
                onAddAtPlayhead={onAddAnimationAtPlayhead}
                hasSelectedCharacter={hasSelectedCharacter}
                onDeleteAnimation={handleDeleteAnimationClick}
              />
              
              {/* Community folder - Shared animations */}
              <AnimationFolder
                name="Community"
                icon={Globe}
                animations={effectiveLibrary.community}
                isOpen={openFolders.community}
                onToggle={() => toggleFolder('community')}
                onDragStart={onAnimationDragStart}
                onAddAtPlayhead={onAddAnimationAtPlayhead}
                hasSelectedCharacter={hasSelectedCharacter}
              />
            </div>
          </ScrollArea>
        </>
      )}

      {activeTab === 'sets' && (
        <PlaceholderContent 
          title="Sets" 
          description="Scene backgrounds coming soon" 
        />
      )}

      {activeTab === 'items' && (
        <PlaceholderContent 
          title="Items" 
          description="Props & objects coming soon" 
        />
      )}
      
      {/* Delete Animation Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              Delete Signature Animation?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone. The animation "{animationToDelete?.name}" will be permanently deleted from your library.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteModalOpen(false);
                setAnimationToDelete(null);
              }}
              className="border-slate-600"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-animation-btn"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
